import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { ReactComponent as Dota2Icon } from './icons/dota-2.svg';

const loginById = async (id) => {
	try {
		const request = await axios.post('/id_login', { id });
		const { isValid, user } = request.data;

		return isValid ? { token: isValid, user } : null;
	} catch (error) {
		if (error.response?.status === 429) {
			return {
				error: 'rate-limit',
				retryAfterSeconds: error.response.data?.retryAfterSeconds,
			};
		}

		return null;
	}
};

const styles = {
	page: {
		alignItems: 'center',
		background: 'linear-gradient(135deg, #111111 0%, #1d1d1d 48%, #27180f 100%)',
		boxSizing: 'border-box',
		color: '#ffffff',
		display: 'flex',
		justifyContent: 'center',
		minWidth: '100vw',
		minHeight: '100vh',
		padding: '24px',
	},
	shell: {
		display: 'grid',
		gridTemplateColumns: 'minmax(240px, 0.9fr) minmax(300px, 420px)',
		maxWidth: 920,
		minHeight: 420,
		width: '100%',
		background: 'rgba(20, 20, 20, 0.88)',
		border: '1px solid rgba(255, 255, 255, 0.08)',
		borderRadius: 8,
		boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
		overflow: 'hidden',
	},
	brand: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		padding: '40px',
		background: '#151515',
		borderRight: '1px solid rgba(255, 255, 255, 0.08)',
	},
	logoWrap: {
		alignItems: 'center',
		display: 'flex',
		gap: 14,
	},
	logo: {
		width: 48,
		height: 48,
		fill: '#fd8e32',
	},
	formWrap: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		padding: '40px',
	},
	form: {
		display: 'grid',
		gap: 18,
	},
	input: {
		background: '#171717',
		border: '1px solid rgba(253, 142, 50, 0.34)',
		borderRadius: 4,
	},
	inputText: {
		color: '#ffffff',
		letterSpacing: 0,
	},
	inputLabel: {
		color: 'rgba(255, 255, 255, 0.68)',
	},
	inputIcon: {
		color: '#fd8e32',
	},
	button: {
		background: '#fd8e32',
		color: '#111111',
		fontWeight: 700,
		height: 48,
		textTransform: 'none',
		transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease',
	},
	helper: {
		color: 'rgba(255, 255, 255, 0.64)',
		margin: '0 0 6px',
	},
	error: {
		color: '#fd8e32',
		margin: 0,
		minHeight: 20,
	},
};

const TelegramLogin = ({ setToken }) => {
	const navigate = useNavigate();
	const isSmallScreen = useMediaQuery('(max-width: 720px)');
	const [id, setId] = useState('');
	const [error, setError] = useState('');
	const [isLoading, setLoading] = useState(false);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');
		setLoading(true);

		const result = await loginById(id);
		setLoading(false);

		if (result?.error === 'rate-limit') {
			const retryAfter = result.retryAfterSeconds
				? ` Try again in ${result.retryAfterSeconds} seconds.`
				: ' Try again later.';
			setError(`Too many attempts.${retryAfter}`);
			return;
		}

		if (!result) {
			setError('User ID is disabled, invalid, or the server is unavailable.');
			return;
		}

		try {
			localStorage.setItem('ggs_token', result.token);
			localStorage.setItem('ggs_user', JSON.stringify(result.user || {}));
			setToken(result.token);
			navigate('/');
		} catch (error) {
			setError('Could not save the session in this browser.');
		}
	};

	return (
		<div style={styles.page}>
			<div style={{
				...styles.shell,
				gridTemplateColumns: isSmallScreen ? '1fr' : styles.shell.gridTemplateColumns,
				minHeight: isSmallScreen ? 'auto' : styles.shell.minHeight,
			}}>
				<section style={{
					...styles.brand,
					borderRight: isSmallScreen ? 0 : styles.brand.borderRight,
					borderBottom: isSmallScreen ? '1px solid rgba(255, 255, 255, 0.08)' : 0,
					padding: isSmallScreen ? 24 : styles.brand.padding,
				}}>
					<div style={styles.logoWrap}>
						<Dota2Icon style={styles.logo} />
						<div>
							<Typography variant="h4">GGS Bot</Typography>
							<Typography style={styles.helper}>Manual Dota 2 analysis</Typography>
						</div>
					</div>
				</section>

				<div style={{
					...styles.formWrap,
					padding: isSmallScreen ? 24 : styles.formWrap.padding,
				}}>
					<form style={styles.form} onSubmit={handleSubmit}>
						<LockOutlinedIcon style={{ color: '#fd8e32', fontSize: 34 }} />
						<div>
							<Typography variant="h5">Sign in</Typography>
							<Typography style={styles.helper}>Enter your personal user ID.</Typography>
						</div>
						<TextField
							autoFocus
							fullWidth
							variant="outlined"
							label="User ID"
							value={id}
							onChange={(event) => setId(event.target.value)}
							style={styles.input}
							InputLabelProps={{
								style: styles.inputLabel,
							}}
							InputProps={{
								style: styles.inputText,
								startAdornment: (
									<InputAdornment position="start">
										<VpnKeyIcon style={styles.inputIcon} />
									</InputAdornment>
								),
							}}
						/>
						<Button
							disabled={isLoading || !id.trim()}
							startIcon={<LockOutlinedIcon />}
							style={styles.button}
							type="submit"
							variant="contained"
						>
							{isLoading ? 'Checking...' : 'Continue'}
						</Button>
						<p aria-live="polite" style={styles.error}>
							{error}
						</p>
					</form>
				</div>
			</div>
		</div>
	);
};

export default TelegramLogin;
